/**
 * Parcours de candidature formateur en 8 étapes (30/10/2026), repris du premier SaaS (« /candidature » du 11/10/2026)
 * à la demande de la direction : « il y avait des illustrations, et les questions étaient plus logiques avec des menus
 * déroulants ».
 *
 * Ce qui vient de l'ancien : l'ordre des questions, une question par écran, l'illustration et le message de chaque
 * étape, les options des listes, le récapitulatif modifiable avant envoi.
 * Ce qui reste du nouveau : le compte est créé d'abord (les pièces sont déposées sur le serveur, jamais en anonyme),
 * chaque étape est ENREGISTRÉE SUR LE SERVEUR quand on clique sur « Continuer » (reprise sur n'importe quel appareil),
 * la validation serveur (mêmes règles, module `domaine/candidature/questionnaire`), les justificatifs avec date
 * d'échéance et l'envoi bloqué tant qu'il manque une pièce exigée.
 *
 * Accessibilité (RGAA 4.1 / WCAG 2.2) : titre d'étape focalisé et annoncé à chaque changement, « Étape n sur 8 » dans
 * le titre du document, résumé des erreurs cliquable, champs reliés à leur message (aria-describedby), cases et listes
 * natives utilisables au clavier, transitions coupées si l'utilisateur réduit les animations.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  ArrowRight,
  BookOpen,
  Check,
  CircleAlert,
  FileText,
  Pencil,
  Trash2,
  Upload,
  type LucideIcon,
} from "lucide-react";
import {
  DETAILS_MODALITES,
  domainesDepuisLignes,
  ETAPES,
  EXPERIENCES,
  libelleDomaine,
  libelleExperience,
  libelleModalite,
  libellePublic,
  lignesDomaines,
  MODALITES,
  NB_ETAPES,
  normaliserLien,
  piecesExigees,
  premiereEtapeIncomplete,
  progression,
  publicAffiche,
  PUBLICS,
  REGIONS_DREETS,
  SANS_STRUCTURE,
  siretAttendu,
  STATUTS_CANDIDATURE,
  validerEtape,
  type Erreurs,
  type EtapeId,
  type LigneDomaine,
  type Reponses,
} from "@/domaine/candidature/questionnaire";
import { api, dateFr, ErreurApi, octets, type Candidature } from "../api";
import { Alerte, Bouton, Champ, cx, Selecteur, useNotifier, useTitreDocument, ZoneTexte } from "../ui/base";
import { ChampDomaines } from "../ui/champs";
import { BandeauBrouillon } from "../ui/BandeauBrouillon";
import { ChampSiret } from "../ui/ChampSiret";
import { purgerDonneesLocales, useBrouillonLocal } from "../ui/brouillon";

const ILLUSTRATIONS: Array<{ src: string; alt: string }> = [
  { src: "/candidature/etape-1.webp", alt: "Formatrice souriante, tablette en main, au milieu de ses stagiaires" },
  { src: "/candidature/etape-2.webp", alt: "Formateur animant une séance devant un paperboard, face à un groupe attentif" },
  { src: "/candidature/etape-3.webp", alt: "Groupe de stagiaires en salle de formation lumineuse" },
  { src: "/candidature/etape-4.webp", alt: "Formatrice animant une séance hybride, participants en salle et en visioconférence" },
  { src: "/candidature/etape-5.webp", alt: "Formateur indépendant souriant devant son ordinateur portable" },
  { src: "/candidature/etape-6.webp", alt: "Formatrice relisant ses documents pédagogiques à son bureau" },
  { src: "/candidature/etape-7.webp", alt: "Équipe de formateurs souriants réunis autour d'une table" },
  { src: "/candidature/etape-8.webp", alt: "Formatrice se connectant à son espace en ligne depuis son ordinateur" },
];

/** Aide affichée sous chaque pièce (reprise de l'ancien parcours pour CV, formations et déroulé). */
const AIDES_PIECES: Record<string, string> = {
  cv: "Parcours, missions, références.",
  identite: "Carte d'identité ou passeport en cours de validité.",
  diplome: "Diplômes, titres ou certifications en lien avec vos domaines.",
  programme: "Le programme des formations que vous proposez ou que vous avez déjà animées.",
  deroule: "Un déroulé pédagogique : objectifs, timing, activités, évaluations.",
  nda: "Le récépissé de votre déclaration d'activité (DREETS).",
  kbis: "Moins de trois mois, si vous avez une structure.",
  attestation: "URSSAF : indiquez la date de fin de validité.",
  casier: "Bulletin n° 3, à demander gratuitement en ligne.",
  autre: "Tout document utile à l'étude de votre candidature.",
};
const AVEC_ECHEANCE = new Set(["attestation", "kbis", "identite"]);
const FORMATS = ".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.odt,.xls,.xlsx,.ods,.csv,.txt";

/** Typographie française : espace insécable avant « ? », « ! », « : » et « ; » (pas de ligne qui commence par « ? »). */
export const insecables = (t: string) => t.replace(/ ([?!:;»])/g, "\u00a0$1").replace(/« /g, "«\u00a0");

const CLE_ETAPE = (formateurId: string) => `s4m:candidature:etape:${formateurId}`;

function lireEtape(formateurId: string): number | null {
  try {
    const v = localStorage.getItem(CLE_ETAPE(formateurId));
    const n = v === null ? NaN : Number(v);
    return Number.isInteger(n) && n >= 0 && n < NB_ETAPES ? n : null;
  } catch {
    return null;
  }
}
function ecrireEtape(formateurId: string, etape: number) {
  try {
    localStorage.setItem(CLE_ETAPE(formateurId), String(etape));
  } catch {
    /* stockage indisponible : la reprise se fera à la première étape incomplète */
  }
}

export function reponsesDepuis(c: Candidature): Reponses {
  const f = c.formateur;
  return {
    formateur_prenom: f.formateur_prenom,
    formateur_nom: f.formateur_nom,
    formateur_telephone: f.formateur_telephone,
    formateur_domaines: (f.formateur_domaines ?? []) as string[],
    formateur_domaine_autre: f.formateur_domaine_autre,
    formateur_specialite: f.formateur_specialite,
    formateur_experience: f.formateur_experience,
    formateur_publics: (f.formateur_publics ?? []) as string[],
    formateur_modalites: (f.formateur_modalites ?? []) as string[],
    formateur_linkedin: f.formateur_linkedin,
    formateur_site_structure: f.formateur_site_structure,
    formateur_statut_juridique: f.formateur_statut_juridique,
    formateur_entreprise_siret: f.formateur_entreprise_siret,
    formateur_entreprise_nom: f.formateur_entreprise_nom,
    formateur_entreprise_adresse: f.formateur_entreprise_adresse,
    formateur_nda_numero: f.formateur_nda_numero,
    formateur_sans_nda: f.formateur_sans_nda,
    formateur_dreets_region: f.formateur_dreets_region,
    formateur_message: f.formateur_message,
    engagement_qualite: f.engagement_qualite_le !== null,
  };
}

export function ParcoursCandidature({ c, surMaj }: { c: Candidature; surMaj: (c: Candidature) => void }) {
  const requetes = useQueryClient();
  const notifier = useNotifier();
  const enregistrees = useMemo(() => reponsesDepuis(c), [c]);
  const [r, setR] = useState<Reponses>(enregistrees);
  const brouillon = useBrouillonLocal(`candidature:${c.formateur.id}`, r, enregistrees);
  const [etape, setEtape] = useState(() => {
    const premiere = premiereEtapeIncomplete(enregistrees, c.pieces, c.types);
    const memorisee = lireEtape(c.formateur.id);
    // On reprend là où l'on s'était arrêté, sans jamais sauter une étape encore incomplète.
    if (memorisee === null) return premiere === -1 ? NB_ETAPES - 1 : premiere;
    return premiere === -1 ? memorisee : Math.min(memorisee, premiere);
  });
  const [erreurs, setErreurs] = useState<Erreurs>({});
  // « Avez-vous un NDA ? » : « non » est enregistré (`formateur_sans_nda`, 31/10/2026) et dispense du récépissé.
  const [aNda, setANda] = useState(
    enregistrees.formateur_nda_numero ? "oui" : enregistrees.formateur_sans_nda ? "non" : "",
  );
  const [depuisRecap, setDepuisRecap] = useState(false);
  const titreRef = useRef<HTMLHeadingElement>(null);
  const resumeRef = useRef<HTMLDivElement>(null);
  const premierRendu = useRef(true);
  const scene = ETAPES[etape]!;
  const derniere = etape === NB_ETAPES - 1;
  useTitreDocument(`Étape ${etape + 1} sur ${NB_ETAPES} : ${scene.question} — Ma candidature`);

  useEffect(() => {
    ecrireEtape(c.formateur.id, etape);
    if (premierRendu.current) {
      premierRendu.current = false;
      return;
    }
    titreRef.current?.focus();
    titreRef.current?.scrollIntoView({ block: "nearest" });
  }, [etape, c.formateur.id]);

  const maj = <K extends keyof Reponses>(cle: K, valeur: Reponses[K]) => {
    setR((x) => ({ ...x, [cle]: valeur }));
    if (erreurs[cle]) setErreurs(({ [cle]: _retire, ...reste }) => reste);
  };

  const enregistrer = useMutation({
    mutationFn: () => {
      const { formateur_prenom, formateur_nom, ...reste } = r;
      // Nom et prénom figent après validation ; tant que la candidature est en brouillon, ils se corrigent ici.
      return api.patch<Candidature>("/candidature", {
        ...reste,
        formateur_prenom,
        formateur_nom,
        formateur_linkedin: normaliserLien(reste.formateur_linkedin),
        formateur_site_structure: normaliserLien(reste.formateur_site_structure),
      });
    },
  });
  const soumettre = useMutation({
    mutationFn: () => api.post<Candidature>("/candidature/soumettre"),
    onSuccess: async (x) => {
      purgerDonneesLocales(); // revue M-9 : le brouillon local n'a plus lieu d'être une fois la candidature envoyée
      surMaj(x);
      await requetes.invalidateQueries({ queryKey: ["moi"] });
      notifier("succes", "Candidature envoyée. Vous recevrez la décision par e-mail.");
    },
  });

  const allerA = (i: number) => {
    setErreurs({});
    setEtape(Math.min(Math.max(i, 0), NB_ETAPES - 1));
  };

  async function continuer() {
    const e = validerEtape(scene.id, r, c.pieces, c.types);
    if (scene.id === "structure") {
      if (!aNda) e.a_nda = "Répondez par oui ou par non.";
      else if (aNda === "oui" && !r.formateur_nda_numero.trim()) e.formateur_nda_numero = "Indiquez votre numéro de déclaration d'activité (11 chiffres).";
    }
    if (Object.keys(e).length > 0) {
      setErreurs(e);
      requestAnimationFrame(() => resumeRef.current?.focus());
      return;
    }
    try {
      const maj = await enregistrer.mutateAsync();
      surMaj(maj);
      brouillon.oublier(reponsesDepuis(maj));
      setErreurs({});
      allerA(depuisRecap ? NB_ETAPES - 1 : etape + 1);
      setDepuisRecap(false);
    } catch (err) {
      const champs = err instanceof ErreurApi ? (err.details?.champs ?? {}) : {};
      if (Object.keys(champs).length > 0) setErreurs(champs);
      else setErreurs({ serveur: err instanceof Error ? err.message : "Enregistrement impossible. Réessayez." });
      requestAnimationFrame(() => resumeRef.current?.focus());
    }
  }

  const listeErreurs = Object.entries(erreurs).filter(([, v]) => v);
  const illustration = ILLUSTRATIONS[etape]!;

  return (
    <div className="overflow-hidden rounded-md border border-trait bg-carte shadow-carte lg:grid lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
      {/* Illustration de l'étape : bandeau sur téléphone, colonne fixe sur grand écran (comme l'ancien parcours). */}
      <aside className="relative h-36 overflow-hidden bg-accent-fort sm:h-48 lg:h-auto lg:min-h-[640px]">
        <img key={illustration.src} src={illustration.src} alt={illustration.alt} width={720} height={960} decoding="async" className="absolute inset-0 size-full object-cover motion-safe:animate-[apparaitre_500ms_ease-out]" />
        <div className="absolute inset-0 bg-gradient-to-t from-encre/90 via-encre/35 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 p-4 text-sur-accent sm:p-6 lg:p-8">
          <p className="max-w-sm font-display text-[15px] leading-snug font-semibold text-balance sm:text-lg lg:text-[26px] lg:leading-[1.2]">{insecables(scene.message)}</p>
          <ol className="mt-6 hidden gap-1.5 text-[13px] lg:grid" aria-label="Les étapes de la candidature">
            {ETAPES.map((e, i) => {
              const etat = i < etape ? "fait" : i === etape ? "actif" : "avenir";
              return (
                <li key={e.id} aria-current={etat === "actif" ? "step" : undefined} className={cx("flex items-center gap-2.5", etat === "avenir" && "opacity-60")}>
                  <span className={cx("inline-flex size-6 items-center justify-center rounded-full text-[11px] font-semibold", etat === "fait" && "bg-sur-accent text-accent-fort", etat === "actif" && "bg-attente text-encre", etat === "avenir" && "border border-sur-accent/60")}>
                    {etat === "fait" ? <Check className="size-3.5" strokeWidth={3} aria-hidden /> : i + 1}
                  </span>
                  {etat === "fait" ? (
                    <button type="button" onClick={() => { setDepuisRecap(false); allerA(i); }} className="rounded-xs text-left underline-offset-4 hover:underline">
                      {e.titre}
                      <span className="sr-only"> (terminée — revenir à cette étape)</span>
                    </button>
                  ) : (
                    <span className={etat === "actif" ? "font-semibold" : undefined}>{e.titre}</span>
                  )}
                </li>
              );
            })}
          </ol>
        </div>
      </aside>

      <div className="min-w-0 px-4 py-6 sm:px-8 sm:py-8 lg:px-10">
        <div className="flex items-baseline justify-between gap-3 text-[13px]">
          <span className="font-semibold text-accent">Étape {etape + 1} sur {NB_ETAPES}</span>
          <span className="text-encre-3">{scene.titre}</span>
        </div>
        <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-papier-3" role="progressbar" aria-label="Progression de la candidature" aria-valuemin={0} aria-valuemax={100} aria-valuenow={progression(etape)} aria-valuetext={`Étape ${etape + 1} sur ${NB_ETAPES}`}>
          <div className="h-full rounded-full bg-accent transition-[width] duration-500 ease-out motion-reduce:transition-none" style={{ width: `${progression(etape)}%` }} />
        </div>

        {brouillon.propose && (
          <div className="mt-5">
            <BandeauBrouillon le={brouillon.propose.le} reprendre={() => { const v = brouillon.accepter(); if (v) setR(v); }} ignorer={brouillon.ignorer} />
          </div>
        )}

        <form
          noValidate
          className="mt-6"
          onSubmit={(e) => {
            e.preventDefault();
            if (derniere) soumettre.mutate();
            else void continuer();
          }}
        >
          <h2 ref={titreRef} tabIndex={-1} className="font-display text-[26px] leading-tight font-semibold text-encre outline-none sm:text-[30px]">
            {insecables(scene.question)}
          </h2>
          <p className="mt-1.5 text-encre-2">{insecables(scene.aide)}</p>

          {listeErreurs.length > 0 && (
            <div ref={resumeRef} tabIndex={-1} role="alert" className="mt-5 rounded-md border border-danger/40 bg-danger-doux px-4 py-3 text-sm text-danger-encre outline-none focus-visible:ring-2 focus-visible:ring-danger/40">
              <p className="flex items-center gap-2 font-semibold"><CircleAlert className="size-4 shrink-0" aria-hidden />{listeErreurs.length === 1 ? "Une réponse est à corriger :" : `${listeErreurs.length} réponses sont à corriger :`}</p>
              <ul className="mt-1.5 list-disc space-y-0.5 pl-6">
                {listeErreurs.map(([cle, msg]) => (
                  <li key={cle}>
                    <a href={`#q-${cle}`} className="underline underline-offset-2" onClick={(e) => { const cible = document.getElementById(`q-${cle}`); if (cible) { e.preventDefault(); (cible.matches("input,select,textarea,button") ? cible : cible.querySelector<HTMLElement>("input,select,textarea,button"))?.focus(); } }}>
                      {msg}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="mt-7 space-y-6">
            <ContenuEtape id={scene.id} r={r} maj={maj} erreurs={erreurs} c={c} surMaj={surMaj} allerA={(i) => { setDepuisRecap(true); allerA(i); }} aNda={aNda} setANda={(v) => { setANda(v); setErreurs(({ a_nda: _x, formateur_nda_numero: _y, ...reste }) => reste); }} />
          </div>

          {derniere && (
            <div className="mt-6 space-y-3">
              {c.manques.length > 0 && (
                <Alerte ton="attention" titre="Avant l'envoi, il manque :">
                  {c.manques.join(" · ")}
                </Alerte>
              )}
              {soumettre.error && <Alerte ton="danger">{soumettre.error.message}</Alerte>}
            </div>
          )}

          {/* Téléphone : l'action principale en pleine largeur, « Retour » dessous ; grand écran : Retour à gauche, action à droite. */}
          <div className="mt-8 flex flex-col-reverse gap-3 border-t border-trait pt-5 sm:flex-row sm:items-center">
            {etape > 0 && (
              <Bouton type="button" variante="discret" icone={<ArrowLeft className="size-4" aria-hidden />} onClick={() => { setDepuisRecap(false); allerA(etape - 1); }}>
                Retour
              </Bouton>
            )}
            <span className="hidden sm:ml-auto sm:block" />
            {derniere ? (
              <Bouton type="submit" variante="primaire" className="w-full sm:w-auto" disabled={c.manques.length > 0} enCours={soumettre.isPending}>
                Envoyer ma candidature
              </Bouton>
            ) : (
              <Bouton type="submit" variante="primaire" className="w-full sm:w-auto" enCours={enregistrer.isPending}>
                {depuisRecap ? "Enregistrer et revenir au récapitulatif" : "Enregistrer et continuer"}
                <ArrowRight className="size-4" aria-hidden />
              </Bouton>
            )}
          </div>
          {!derniere && <p className="mt-3 text-right text-[12.5px] text-encre-3">Vos réponses sont enregistrées à chaque étape : vous pouvez reprendre plus tard, depuis n'importe quel appareil.</p>}
        </form>
      </div>
    </div>
  );
}

// ——— Contenu de chaque étape ———

type PropsEtape = {
  id: EtapeId;
  r: Reponses;
  maj: <K extends keyof Reponses>(cle: K, valeur: Reponses[K]) => void;
  erreurs: Erreurs;
  c: Candidature;
  surMaj: (c: Candidature) => void;
  allerA: (i: number) => void;
  aNda: string;
  setANda: (v: string) => void;
};

function ContenuEtape({ id, r, maj, erreurs, c, surMaj, allerA, aNda, setANda }: PropsEtape) {
  const texte = (cle: keyof Reponses & string) => ({
    id: `q-${cle}`,
    value: r[cle] as string,
    erreur: erreurs[cle],
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => maj(cle, e.target.value as never),
  });
  switch (id) {
    case "vous":
      return (
        <>
          <div className="grid gap-5 sm:grid-cols-2">
            <Champ libelle="Prénom" required autoComplete="given-name" maxLength={100} {...texte("formateur_prenom")} />
            <Champ libelle="Nom" required autoComplete="family-name" maxLength={100} {...texte("formateur_nom")} />
          </div>
          <Champ libelle="Téléphone" required type="tel" inputMode="tel" autoComplete="tel" maxLength={30} aide="Par exemple 06 12 34 56 78 ou +41 61 000 00 00 — pour un premier échange avec l'équipe." {...texte("formateur_telephone")} />
          <p className="rounded-md bg-papier-2 px-3 py-2.5 text-[13px] text-encre-2">
            Adresse de connexion : <span className="font-medium text-encre">{c.formateur.formateur_email}</span> — c'est à elle que la décision sera envoyée.
          </p>
        </>
      );
    case "expertise":
      return (
        <>
          {/* 31/10/2026 : menu déroulant (catégories du catalogue de la vitrine + « Autre »), un menu par domaine. */}
          <DomainesEtape
            r={r}
            maj={maj}
            erreur={erreurs.formateur_domaines ?? erreurs.formateur_domaine_autre}
          />
          <Champ
            libelle="Votre spécialité en une phrase (facultatif)"
            maxLength={200}
            placeholder="Ex. J'aide les managers de proximité à conduire le changement."
            aide="Reprise dans la présentation de votre profil."
            {...texte("formateur_specialite")}
          />
        </>
      );
    case "experience":
      return (
        <>
          <Selecteur libelle="Depuis combien de temps formez-vous ?" required {...texte("formateur_experience")}>
            <option value="">— Choisir —</option>
            {EXPERIENCES.map((e) => (
              <option key={e.valeur} value={e.valeur}>{e.libelle} — {e.detail}</option>
            ))}
          </Selecteur>
          <CartesChoix
            nom="formateur_publics"
            legende="Quels publics formez-vous ?"
            multiple
            colonnes={2}
            options={PUBLICS.map((p) => ({ valeur: p.valeur, libelle: p.libelle }))}
            valeurs={r.formateur_publics.filter(publicAffiche)}
            onChange={(v) =>
              maj("formateur_publics", [...v, ...r.formateur_publics.filter((p) => !publicAffiche(p))])
            }
            erreur={erreurs.formateur_publics}
          />
        </>
      );
    case "pratique":
      return (
        <>
          <CartesChoix
            nom="formateur_modalites"
            legende="Vos modalités"
            multiple
            colonnes={3}
            options={MODALITES.map((m) => ({
              valeur: m.valeur,
              libelle: m.valeur === "mixte" ? "Mixte" : m.libelle,
              detail: DETAILS_MODALITES[m.valeur],
            }))}
            valeurs={r.formateur_modalites}
            onChange={(v) => maj("formateur_modalites", v)}
            erreur={erreurs.formateur_modalites}
          />
          <Champ
            libelle="Votre profil LinkedIn (facultatif)"
            type="url"
            inputMode="url"
            maxLength={300}
            placeholder="linkedin.com/in/votre-nom"
            {...texte("formateur_linkedin")}
          />
        </>
      );
    case "structure": {
      const siret = siretAttendu(r.formateur_statut_juridique);
      return (
        <>
          <Selecteur libelle="Votre situation" required aide="Micro-entreprise, société, portage… Pas encore de structure ? Choisissez-le, ce n'est pas bloquant." {...texte("formateur_statut_juridique")}>
            <option value="">— Choisir —</option>
            {STATUTS_CANDIDATURE.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </Selecteur>
          {r.formateur_statut_juridique === SANS_STRUCTURE && <Alerte>Aucun souci : l'équipe vous conseillera sur le statut le plus adapté lors de votre premier échange.</Alerte>}
          {r.formateur_statut_juridique && r.formateur_statut_juridique !== SANS_STRUCTURE && (
            <div className="grid gap-5 rounded-md bg-papier-2 p-4 sm:grid-cols-2">
              <ChampSiret
                libelle={siret ? "SIRET" : "SIRET (si vous en avez un)"}
                required={siret}
                id="q-formateur_entreprise_siret"
                value={r.formateur_entreprise_siret}
                erreur={erreurs.formateur_entreprise_siret}
                aide="14 chiffres, ou le nom de votre structure : raison sociale et adresse se remplissent depuis l'annuaire officiel."
                onChange={(x) => maj("formateur_entreprise_siret", x)}
                onChoisir={(e) => {
                  maj("formateur_entreprise_siret", e.siret);
                  maj("formateur_entreprise_nom", e.raison_sociale);
                  if (e.adresse) maj("formateur_entreprise_adresse", e.adresse);
                }}
              />
              <Champ libelle="Raison sociale (facultatif)" autoComplete="organization" maxLength={200} {...texte("formateur_entreprise_nom")} />
              <div className="sm:col-span-2">
                <Champ libelle="Adresse (facultatif)" autoComplete="street-address" maxLength={400} {...texte("formateur_entreprise_adresse")} />
              </div>
              <div className="sm:col-span-2">
                <Champ
                  libelle="Site de la structure (facultatif)"
                  type="url"
                  inputMode="url"
                  maxLength={300}
                  placeholder="www.ma-structure.fr"
                  {...texte("formateur_site_structure")}
                />
              </div>
            </div>
          )}
          <Selecteur
            libelle="Avez-vous un numéro de déclaration d'activité (NDA) ?"
            id="q-a_nda"
            value={aNda}
            erreur={erreurs.a_nda}
            onChange={(e) => {
              setANda(e.target.value);
              maj("formateur_sans_nda", e.target.value === "non");
              if (e.target.value !== "oui") {
                maj("formateur_nda_numero", "");
                maj("formateur_dreets_region", "");
              }
            }}
          >
            <option value="">— Choisir —</option>
            <option value="oui">Oui, j'en ai un</option>
            <option value="non">Non : je n'ai pas de NDA</option>
          </Selecteur>
          {aNda === "oui" ? (
            <div className="grid gap-5 sm:grid-cols-2">
              <Champ libelle="Numéro de déclaration" inputMode="numeric" maxLength={20} placeholder="11 chiffres" {...texte("formateur_nda_numero")} />
              <Selecteur libelle="Région de dépôt (DREETS)" {...texte("formateur_dreets_region")}>
                <option value="">— Choisir une région —</option>
                {REGIONS_DREETS.map((x) => (
                  <option key={x} value={x}>{x}</option>
                ))}
              </Selecteur>
            </div>
          ) : aNda === "non" ? (
            <p className="text-sm text-encre-2">
              Ce n'est pas bloquant : avec le portage, vous formez sous la déclaration de l'organisme. Aucun
              récépissé ne vous sera demandé.
            </p>
          ) : null}
        </>
      );
    }
    case "pieces":
      return <Pieces c={c} surMaj={surMaj} erreurs={erreurs} />;
    case "engagements":
      return (
        <>
          <div id="q-engagement_qualite">
            <label className={cx("flex cursor-pointer gap-4 rounded-md border p-5 text-sm leading-relaxed transition-colors has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-accent/30", r.engagement_qualite ? "border-accent bg-accent-doux" : erreurs.engagement_qualite ? "border-danger/60" : "border-trait-fort")}>
              <input type="checkbox" checked={r.engagement_qualite} onChange={(e) => maj("engagement_qualite", e.target.checked)} aria-invalid={erreurs.engagement_qualite ? true : undefined} aria-describedby={erreurs.engagement_qualite ? "engagement-erreur" : undefined} className="mt-0.5 size-5 shrink-0 accent-[var(--color-accent)]" />
              <span>
                Je m'engage à respecter le référentiel national qualité <strong>Qualiopi</strong> et le code de déontologie de l'organisme, à m'exprimer de manière sincère et à répondre aux demandes de l'organisme en cas de contrôle.
              </span>
            </label>
            {erreurs.engagement_qualite && <p id="engagement-erreur" className="mt-1.5 text-[13px] text-danger">{erreurs.engagement_qualite}</p>}
          </div>
          <ZoneTexte libelle="Un mot pour l'équipe (facultatif)" rows={4} maxLength={1500} placeholder="Vos projets, le volume d'activité envisagé, vos questions…" {...texte("formateur_message")} />
        </>
      );
    case "envoi":
      return <Recapitulatif r={r} c={c} allerA={allerA} />;
  }
}

// ——— Cartes à cocher : choix multiples visuels, au clavier comme des cases natives ———

function CartesChoix({
  nom,
  legende,
  options,
  valeurs,
  onChange,
  erreur,
  multiple = false,
  colonnes = 2,
}: {
  nom: string;
  legende: string;
  options: Array<{ valeur: string; libelle: string; detail?: string; icone?: LucideIcon }>;
  valeurs: string[];
  onChange: (v: string[]) => void;
  erreur?: string;
  multiple?: boolean;
  colonnes?: 1 | 2 | 3;
}) {
  return (
    <fieldset id={`q-${nom}`} className="min-w-0" aria-describedby={erreur ? `${nom}-erreur` : undefined}>
      <legend className="mb-2 text-[13px] font-medium text-encre-2">
        {legende} <span className="text-danger" aria-hidden>*</span>
        {multiple && <span className="ml-1 font-normal text-encre-3">— plusieurs choix possibles</span>}
      </legend>
      <div className={cx("grid gap-2", colonnes === 3 ? "sm:grid-cols-3" : colonnes === 2 ? "sm:grid-cols-2" : "")}>
        {options.map((o) => {
          const actif = valeurs.includes(o.valeur);
          const Icone = o.icone;
          return (
            <label key={o.valeur} className={cx("flex min-h-12 cursor-pointer items-center gap-3 rounded-md border px-3 py-2.5 text-sm transition-colors duration-150 has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-accent/30", actif ? "border-accent bg-accent-doux text-accent-fort" : erreur ? "border-danger/50 bg-carte" : "border-trait-fort bg-carte hover:border-accent")}>
              <input type={multiple ? "checkbox" : "radio"} name={nom} value={o.valeur} checked={actif} onChange={() => onChange(multiple ? (actif ? valeurs.filter((x) => x !== o.valeur) : [...valeurs, o.valeur]) : [o.valeur])} className="sr-only" />
              {Icone && <span className={cx("inline-flex size-8 shrink-0 items-center justify-center rounded-sm", actif ? "bg-accent text-sur-accent" : "bg-papier-2 text-encre-2")}><Icone className="size-4" aria-hidden /></span>}
              <span className="min-w-0 flex-1">
                <span className={cx("block leading-snug", actif && "font-medium")}>{o.libelle}</span>
                {o.detail && <span className="block text-[12.5px] text-encre-3">{o.detail}</span>}
              </span>
              <span aria-hidden className={cx("inline-flex size-5 shrink-0 items-center justify-center rounded-xs border", actif ? "border-accent bg-accent text-sur-accent" : "border-trait-fort")}>{actif && <Check className="size-3.5" strokeWidth={3} />}</span>
            </label>
          );
        })}
      </div>
      {erreur && <p id={`${nom}-erreur`} className="mt-1.5 text-[13px] text-danger">{erreur}</p>}
    </fieldset>
  );
}

// ——— Pièces : déposées tout de suite sur le serveur (contrôle du type et de la taille), glisser-déposer accepté ———

function Pieces({ c, surMaj, erreurs }: { c: Candidature; surMaj: (c: Candidature) => void; erreurs: Erreurs }) {
  const notifier = useNotifier();
  const [expiration, setExpiration] = useState<Record<string, string>>({});
  const [autres, setAutres] = useState(false);
  // La ligne de la pièce passe au vert et affiche le fichier : une annonce discrète suffit (plus de pile de notifications).
  const [annonce, setAnnonce] = useState("");
  const deposer = useMutation({
    mutationFn: (v: { type: string; fichier: File }) => api.fichier<Candidature>("/candidature/pieces", v.fichier, { type: v.type, expire_le: expiration[v.type] ?? "" }),
    onSuccess: (x, v) => { surMaj(x); setAnnonce(`Pièce ajoutée : ${v.fichier.name}.`); },
    onError: (e) => notifier("danger", e.message),
  });
  const retirer = useMutation({
    mutationFn: (id: string) => api.suppr<Candidature>(`/candidature/pieces/${id}`),
    onSuccess: surMaj,
  });
  const exigees = piecesExigees(c.formateur, c.types);
  const essentielles = c.types.filter((t) => exigees.includes(t));
  // Le récépissé de NDA n'a pas lieu d'être proposé à qui a déclaré ne pas avoir de NDA.
  const complementaires = c.types.filter((t) => !essentielles.some((e) => e.type === t.type) && !t.sauf_sans_nda);
  const ligne = (t: (typeof c.types)[number]) => (
    <ZoneDepot
      key={t.type}
      id={`q-piece_${t.type}`}
      libelle={t.libelle}
      aide={AIDES_PIECES[t.type]}
      obligatoire={exigees.includes(t)}
      erreur={erreurs[`piece_${t.type}`]}
      enCours={deposer.isPending && deposer.variables?.type === t.type}
      deposer={(fichier) => deposer.mutate({ type: t.type, fichier })}
      echeance={
        AVEC_ECHEANCE.has(t.type)
          ? {
              value: expiration[t.type] ?? "",
              onChange: (v) => setExpiration({ ...expiration, [t.type]: v }),
            }
          : undefined
      }
    >
      {c.pieces
        .filter((p) => p.type === t.type)
        .map((p) => (
          <div
            key={p.id}
            className="mt-2 flex items-center justify-between gap-2 rounded-sm bg-papier-2 px-3 py-1.5 text-[13px]"
          >
            <span className="flex min-w-0 items-center gap-2">
              <FileText className="size-3.5 shrink-0 text-accent" aria-hidden />
              <span className="truncate">{p.nom_fichier}</span>
              <span className="shrink-0 text-encre-3">
                · {octets(p.taille)}
                {p.expire_le ? ` · valable jusqu'au ${dateFr(p.expire_le)}` : ""}
              </span>
            </span>
            <button
              type="button"
              aria-label={`Retirer ${p.nom_fichier}`}
              onClick={() => confirm(`Retirer « ${p.nom_fichier} » ?`) && retirer.mutate(p.id)}
              className="inline-flex size-8 shrink-0 items-center justify-center rounded-xs text-encre-3 hover:bg-papier-3 hover:text-danger"
            >
              <Trash2 className="size-3.5" aria-hidden />
            </button>
          </div>
        ))}
    </ZoneDepot>
  );
  return (
    <>
      <div className="space-y-3">{essentielles.map(ligne)}</div>
      <details
        open={autres}
        onToggle={(e) => setAutres((e.target as HTMLDetailsElement).open)}
        className="rounded-md border border-trait"
      >
        <summary className="cursor-pointer px-4 py-3 text-sm font-medium">
          Pièces complémentaires (facultatives) : K-bis, attestations URSSAF, casier judiciaire…
        </summary>
        <div className="space-y-3 px-4 pb-4">{complementaires.map(ligne)}</div>
      </details>
      <p className="text-[12.5px] text-encre-3">Formats admis : PDF, image, Word, OpenDocument, Excel ou texte — 15 Mo maximum par fichier.</p>
      <p role="status" className="sr-only">{annonce}</p>
    </>
  );
}

function ZoneDepot({ id, libelle, aide, obligatoire, erreur, enCours, deposer, echeance, children }: { id: string; libelle: string; aide?: string; obligatoire: boolean; erreur?: string; enCours: boolean; deposer: (f: File) => void; echeance?: { value: string; onChange: (v: string) => void }; children: ReactNode }) {
  const ref = useRef<HTMLInputElement>(null);
  const [survol, setSurvol] = useState(false);
  const deposee = Array.isArray(children) ? children.length > 0 : Boolean(children);
  return (
    <div
      id={id}
      onDragOver={(e) => { e.preventDefault(); setSurvol(true); }}
      onDragLeave={() => setSurvol(false)}
      onDrop={(e) => { e.preventDefault(); setSurvol(false); const f = e.dataTransfer.files?.[0]; if (f) deposer(f); }}
      className={cx("rounded-md border border-dashed p-4 transition-colors", survol ? "border-accent bg-accent-doux" : erreur ? "border-danger/60" : deposee ? "border-accent/50 bg-carte" : "border-trait-fort bg-carte")}
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="flex items-center gap-2 text-sm font-medium">
            {deposee ? <Check className="size-4 text-valide" aria-hidden /> : <BookOpen className="size-4 text-encre-3" aria-hidden />}
            {libelle}
            {obligatoire ? <span className="rounded-xs bg-attente-doux px-1.5 py-0.5 text-[11px] font-semibold text-attente-encre">Obligatoire</span> : <span className="text-[12px] font-normal text-encre-3">facultatif</span>}
          </p>
          {aide && <p className="mt-0.5 text-[13px] text-encre-3">{aide}</p>}
        </div>
        <span className="flex flex-wrap items-center gap-2">
          {echeance && (
            <label className="flex items-center gap-1.5 text-[12.5px] text-encre-3">
              Fin de validité (facultatif)
              <input type="date" value={echeance.value} onChange={(e) => echeance.onChange(e.target.value)} className="h-8 rounded-sm border border-trait-fort bg-carte px-2 text-[13px]" />
            </label>
          )}
          <input ref={ref} type="file" accept={FORMATS} className="sr-only" tabIndex={-1} aria-label={`Fichier : ${libelle}`} onChange={(e) => { const f = e.target.files?.[0]; if (f) deposer(f); e.target.value = ""; }} />
          <Bouton taille="sm" variante="secondaire" enCours={enCours} icone={<Upload className="size-4" aria-hidden />} aria-label={`Ajouter : ${libelle}`} onClick={() => ref.current?.click()}>
            Ajouter
          </Bouton>
        </span>
      </div>
      <p className="mt-1 hidden text-[12px] text-encre-3 sm:block">ou glissez le fichier ici</p>
      {children}
      {erreur && <p className="mt-1.5 text-[13px] text-danger">{erreur}</p>}
    </div>
  );
}

// ——— Récapitulatif : chaque ligne renvoie à son étape ———

function Recapitulatif({ r, c, allerA }: { r: Reponses; c: Candidature; allerA: (i: number) => void }) {
  const lignes: Array<{ etape: number; titre: string; valeur: string }> = [
    {
      etape: 0,
      titre: "Vous",
      valeur: [`${r.formateur_prenom} ${r.formateur_nom}`.trim(), r.formateur_telephone]
        .filter(Boolean)
        .join(" · "),
    },
    {
      etape: 1,
      titre: "Domaine d'expertise",
      valeur:
        r.formateur_domaines.map((d) => libelleDomaine(d, r.formateur_domaine_autre)).join(", ") +
        (r.formateur_specialite ? ` — « ${r.formateur_specialite} »` : ""),
    },
    {
      etape: 2,
      titre: "Expérience",
      valeur: [
        r.formateur_experience ? libelleExperience(r.formateur_experience) : "",
        r.formateur_publics.filter(publicAffiche).map(libellePublic).join(", "),
      ]
        .filter(Boolean)
        .join(" · "),
    },
    {
      etape: 3,
      titre: "Façon de former",
      valeur: [r.formateur_modalites.map(libelleModalite).join(", "), r.formateur_linkedin]
        .filter(Boolean)
        .join(" · "),
    },
    {
      etape: 4,
      titre: "Structure",
      valeur: [
        r.formateur_statut_juridique,
        r.formateur_entreprise_nom,
        r.formateur_entreprise_siret ? `SIRET ${r.formateur_entreprise_siret}` : "",
        r.formateur_nda_numero ? `NDA ${r.formateur_nda_numero}` : r.formateur_sans_nda ? "Pas de NDA" : "",
        r.formateur_site_structure,
      ]
        .filter(Boolean)
        .join(" · "),
    },
    {
      etape: 5,
      titre: "Pièces",
      valeur: c.types
        .filter((t) => c.pieces.some((p) => p.type === t.type))
        .map((t) => t.libelle)
        .join(", "),
    },
    { etape: 6, titre: "Engagements", valeur: r.engagement_qualite ? "Engagement qualité accepté" : "" },
  ];
  return (
    <dl className="divide-y divide-trait rounded-md border border-trait">
      {lignes.map((l) => (
        <div key={l.titre} className="flex items-start gap-3 px-4 py-3">
          <div className="min-w-0 flex-1">
            <dt className="text-[12.5px] font-medium text-encre-3">{l.titre}</dt>
            <dd className={cx("mt-0.5 line-clamp-3 text-sm whitespace-pre-line", !l.valeur && "font-medium text-danger-encre")}>{l.valeur || "À compléter"}</dd>
          </div>
          <button type="button" onClick={() => allerA(l.etape)} className="inline-flex min-h-8 shrink-0 items-center gap-1.5 rounded-sm px-2 text-[13px] font-medium text-accent hover:bg-accent-doux">
            <Pencil className="size-3.5" aria-hidden /> Modifier<span className="sr-only"> : {l.titre}</span>
          </button>
        </div>
      ))}
    </dl>
  );
}

/** Domaines de l'étape « Expertise » : lignes locales (une ligne vide ajoutée reste affichée), réponses mises à jour à chaque choix. */
function DomainesEtape({ r, maj, erreur }: { r: Reponses; maj: PropsEtape["maj"]; erreur?: string }) {
  const [lignes, setLignes] = useState<LigneDomaine[]>(() =>
    lignesDomaines(r.formateur_domaines, r.formateur_domaine_autre),
  );
  return (
    <ChampDomaines
      idPremier="q-formateur_domaines"
      lignes={lignes}
      erreur={erreur}
      onChange={(l) => {
        setLignes(l);
        const d = domainesDepuisLignes(l);
        maj("formateur_domaines", d.formateur_domaines);
        maj("formateur_domaine_autre", d.formateur_domaine_autre);
      }}
    />
  );
}
